import { inject, Injectable, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { getClientTimezone } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { AcceptSignupInvitationRequest } from '../../swagger-api';
import { SignupInvitationForm } from '../auth-form.inteface';
import { AuthShared } from '../auth-shared.abstract';

@Injectable()
export abstract class AcceptSignupInvitationAbstract extends AuthShared<SignupInvitationForm> implements OnInit {
  public readonly DEBOUNCE_TIME: number = AppValues.DEBOUNCE_TIME;

  private route = inject(ActivatedRoute);
  protected operationInfoService = inject(OperationInfoService);

  public validateInvitationToken(): void {
    this.authService.validateInvitationToken(this.extractToken());
  }

  public override ngOnInit(): void {
    this.validateInvitationToken();
    super.ngOnInit();
  }

  public acceptSignupInvitation(): void {
    if (this.form.invalid) {
      this.validateAllFields();
      return;
    }

    const { alias, password } = this.form.getRawValue();

    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'registration-form-register-btn' },
        successfulMessage: 'ACCEPT_ADMIN_SIGNUP_INVITATION_OPERATION_DESCRIPTION_HINT',
      },
      () => {
        this.acceptUserSignupInvitation({
          alias: alias.trim(),
          password: password.trim(),
          token: this.extractToken(),
          timezone: getClientTimezone(),
        });
      },
    );
  }

  public extractToken(): string {
    return this.route.snapshot.queryParams['invitation_token'] as string;
  }

  public abstract get getFormTitle(): string;

  protected abstract acceptUserSignupInvitation(registerIndividualUser: AcceptSignupInvitationRequest): void;

  protected initializeForm(): void {
    const aliasValidators: ValidatorFn[] = [
      ...this.validationService.getValidators('alias'),
      this.validationService.getRequiredValidator(),
    ];

    this.form = new FormGroup(
      {
        alias: new FormControl('', aliasValidators),
        password: new FormControl('', this.validationService.getValidators('password')),
        repeatPassword: new FormControl('', this.validationService.getValidators('repeatPassword')),
        agreeCheck: new FormControl({ value: false, disabled: false }, Validators.requiredTrue),
      },

      this.validationService.getFormPasswordValidators(),
    );

    this.validationService.setMaxInputLength(this.form, 'alias');
    this.validationService.setMaxInputLength(this.form, 'password');

    this.validateRepeatPasswordChanges();
  }
}
